import { createReducer } from '@reduxjs/toolkit';

import { FuelManagementRegisterManager, Loadable } from 'models';
import { makeThunkFromAPICall } from 'state';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';

export type FuelManagementRegisterState = {
    deleteFMRegisterResponse: Loadable<FuelManagementRegisterManager.API.ActionResponse>;
    list: Loadable<FuelManagementRegisterManager.FuelManagementRegister[]>;
    listFromGeoserver: Loadable<
        FuelManagementRegisterManager.FuelManagementRegister[]
    >;
    audit: Loadable<
        FuelManagementRegisterManager.FuelManagementRegisterAuditRecord[]
    >;
    selectedFeature: Loadable<Feature<Geometry>>;
    fmrUploads: Loadable<FuelManagementRegisterManager.DataUpload[]>;
    editFeature: Loadable<FuelManagementRegisterManager.EditFMR>;
    editFmrResponse: Loadable<FuelManagementRegisterManager.FuelManagementRegister>;
    addFmrResponse: Loadable<FuelManagementRegisterManager.FuelManagementRegister>;
};

const initialState: FuelManagementRegisterState = {
    deleteFMRegisterResponse: {
        status: 'idle',
    },
    editFmrResponse: {
        status: 'idle',
    },
    addFmrResponse: {
        status: 'idle',
    },
    list: {
        status: 'idle',
    },
    audit: {
        status: 'idle',
    },
    listFromGeoserver: {
        status: 'idle',
    },
    selectedFeature: {
        status: 'idle',
    },
    fmrUploads: {
        status: 'idle',
    },
    editFeature: {
        status: 'idle',
    },
};

export const [sendDeleteFMRegister, setDeleteFMRegisterResponse] =
    makeThunkFromAPICall(
        FuelManagementRegisterManager.deleteFMRegister,
        'fuelmanagementregister/delete',
    );

export const [editFmr, setEditFmr] = makeThunkFromAPICall(
    FuelManagementRegisterManager.editFmr,
    'fuelmanagementregister/edit',
);

export const [addFmr, setAddFmr] = makeThunkFromAPICall(
    FuelManagementRegisterManager.addFmr,
    'fuelmanagementregister/add',
);

export const [getFuelManagementRegister, setFuelManagementRegister] =
    makeThunkFromAPICall(
        FuelManagementRegisterManager.getFuelManagementRegisterList,
        'fuelmanagementregister/list',
    );

export const [getFuelManagementRegisterAudit, setFuelManagementRegisterAudit] =
    makeThunkFromAPICall(
        FuelManagementRegisterManager.getFuelManagementRegisterAudit,
        'fuelmanagementregister/audit',
    );

export const [
    getFuelManagementRegisterFromGeoserver,
    setFuelManagementRegisterFromGeoserver,
] = makeThunkFromAPICall(
    FuelManagementRegisterManager.getFuelManagementRegisterFromGeoserver,
    'fuelmanagementregister/geoserver/list',
);

export const [
    getSelectedFeatureFromGeoserver,
    setSelectedFeatureFromGeoserver,
] = makeThunkFromAPICall(
    FuelManagementRegisterManager.getSelectedFeatureFromGeoserver,
    'fuelmanagementregister/selected/fmr',
);

export const [batchUpdateFMRegister, setBatchUpdateFMRegisterResponse] =
    makeThunkFromAPICall(
        FuelManagementRegisterManager.batchUpdateFMRegister,
        'fuelmanagementregister/delete',
    );

export const [uploadFMRegister] = makeThunkFromAPICall(
    FuelManagementRegisterManager.uploadFMRegister,
    'fuelmanagementregister/data-upload/fmr',
);

export const [getFMRUploads, setFMRUploads] = makeThunkFromAPICall(
    FuelManagementRegisterManager.getFMRUploads,
    'fuelmanagementregister/data-upload/fmruploads',
);

export const [editFMRDialog, setEditFMRDialog] = makeThunkFromAPICall(
    FuelManagementRegisterManager.editFMRDialog,
    'fuelmanagementregister/edit/feature',
);

export const fuelmanagementregisterReducer = createReducer(
    initialState,
    (builder) => {
        builder.addCase(setDeleteFMRegisterResponse, (state, action) => {
            return {
                ...state,
                deleteFMRegisterResponse: {
                    ...state.deleteFMRegisterResponse,
                    ...action.payload.loadable,
                },
            };
        });

        builder.addCase(setFuelManagementRegister, (state, action) => {
            return {
                ...state,
                list: { ...state.list, ...action.payload.loadable },
            };
        });

        builder.addCase(
            setFuelManagementRegisterFromGeoserver,
            (state, action) => {
                return {
                    ...state,
                    listFromGeoserver: {
                        ...state.listFromGeoserver,
                        ...action.payload.loadable,
                    },
                };
            },
        );

        builder.addCase(setFuelManagementRegisterAudit, (state, action) => {
            return {
                ...state,
                audit: { ...state.audit, ...action.payload.loadable },
            };
        });

        builder.addCase(setSelectedFeatureFromGeoserver, (state, action) => {
            return {
                ...state,
                selectedFeature: {
                    ...state.selectedFeature,
                    ...action.payload.loadable,
                },
            };
        });

        builder.addCase(setEditFmr, (state, action) => {
            return {
                ...state,
                editFmrResponse: {
                    ...state.editFmrResponse,
                    ...action.payload.loadable,
                },
            };
        });

        builder.addCase(setAddFmr, (state, action) => {
            return {
                ...state,
                addFmrResponse: {
                    ...state.addFmrResponse,
                    ...action.payload.loadable,
                },
            };
        });

        builder.addCase(setFMRUploads, (state, action) => {
            return {
                ...state,
                fmrUploads: {
                    ...state.fmrUploads,
                    ...action.payload.loadable,
                },
            };
        });

        builder.addCase(setEditFMRDialog, (state, action) => {
            return {
                ...state,
                editFeature: {
                    ...state.editFeature,
                    ...action.payload.loadable,
                },
            };
        });
    },
);

export const FuelManagementRegisterActions = {
    sendDeleteFMRegister,
    getFuelManagementRegister,
    getFuelManagementRegisterFromGeoserver,
    getFuelManagementRegisterAudit,
    getSelectedFMRFromGeoserver: getSelectedFeatureFromGeoserver,
    batchUpdateFMRegister,
    getFMRUploads,
    uploadFMRegister,
    editFMRDialog,
    editFmr,
    addFmr,
};

export default fuelmanagementregisterReducer;
